import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";

import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import { currenciesApi } from "@/apis/apis";
import { getCurrencyInitialValues } from "@/data/CurrencyManagement/Currencies/initial-values";
import getCurrencySchema from "@/schema/CurrencyManagement/Currencies/schema";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const CurrencyModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id ? `${currenciesApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Currency ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${currenciesApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: currenciesApi,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getCurrencyInitialValues(modalState.id ? showData : null),
    validationSchema: getCurrencySchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    modalState.id ? saveEdit(values) : saveCreate(values);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Currency" : "Add Currency"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="Code"
            hint={"FX Rates will only display valid currencies."}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"symbol"}
            saveError={saveError}
            label="Symbol"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"name"}
            saveError={saveError}
            label="Name"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput
            formik={formik}
            saveError={saveError}
            hint="Activating a currency will trigger a refresh of the FX Rates."
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default CurrencyModal;
